import axios from 'axios';
import { 
  Appointment, 
  TimeSlot, 
  ServiceType, 
  DealershipInfo, 
  ApiResponse, 
  TireWorksHDConfig 
} from '../types';

class TireWorksHDApi {
  private config: TireWorksHDConfig;
  private axiosInstance;

  constructor(config: TireWorksHDConfig) {
    this.config = config;
    this.axiosInstance = axios.create({
      baseURL: config.apiEndpoint,
      timeout: 30000,
      headers: {
        'Content-Type': 'text/xml; charset=utf-8',
        'SOAPAction': ''
      }
    });
  }

  private createSOAPEnvelope(body: string): string {
    return `<?xml version="1.0" encoding="utf-8"?>
      <soap:Envelope xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/" 
                     xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" 
                     xmlns:xsd="http://www.w3.org/2001/XMLSchema">
        <soap:Body>
          ${body}
        </soap:Body>
      </soap:Envelope>`;
  }

  private async makeSOAPRequest(action: string, body: string): Promise<string> {
    try {
      const soapEnvelope = action === 'b2cGetData' || action === 'b2cPutData' 
        ? this.createB2CSOAPEnvelope(action, body)
        : this.createSOAPEnvelope(body);

      // If we have an embed token, route via backend proxy to avoid mixed-content
      if (this.config.embedToken) {
        // Build proxy URL robustly even if REACT_APP_API_URL already ends with /api
        const raw = process.env.REACT_APP_API_URL || '';
        const base = raw.replace(/\/+$/, '');
        const url = /\/api$/.test(base)
          ? `${base}/proxy/soap/${this.config.embedToken}`
          : `${base}/api/proxy/soap/${this.config.embedToken}`;
        // Temporary client-side diagnostics
        // eslint-disable-next-line no-console
        console.log('[Scheduler SOAP] POST proxy URL:', url);
        const resp = await axios.post(url, { action, soapBody: soapEnvelope }, { timeout: 20000 });
        if (!resp.data?.success || !resp.data?.data) {
          throw new Error(resp.data?.error || 'Proxy error');
        }
        return resp.data.data as string;
      }

      const response = await this.axiosInstance.post('', soapEnvelope, {
        headers: {
          'Content-Type': 'text/xml; charset=utf-8',
          'SOAPAction': this.getSOAPAction(action)
        }
      });

      return response.data;
    } catch (error) {
      console.error('SOAP request failed:', error);
      throw new Error(`SOAP request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private createB2CSOAPEnvelope(action: string, body: string): string {
    const namespace = 'http://www.signalextraprise.com/b2cServices';
    if (action === 'b2cPutData') {
      return `<?xml version="1.0" encoding="utf-8"?>
      <soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" 
                     xmlns:xsd="http://www.w3.org/2001/XMLSchema" 
                     xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
        <soap:Body>
          <${action} xmlns="${namespace}">
            <xmlIn>${body}</xmlIn>
          </${action}>
        </soap:Body>
      </soap:Envelope>`;
    }
    return `<?xml version="1.0" encoding="utf-8"?>
      <soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" 
                     xmlns:xsd="http://www.w3.org/2001/XMLSchema" 
                     xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
        <soap:Body>
          <${action} xmlns="${namespace}">
            <xmlIn>
              ${body}
            </xmlIn>
          </${action}>
        </soap:Body>
      </soap:Envelope>`;
  }

  private getSOAPAction(action: string): string {
    const namespace = 'http://www.signalextraprise.com/b2cServices';
    switch (action) {
      case 'b2cGetData':
        return `${namespace}/b2cGetData`;
      case 'b2cPutData':
        return `${namespace}/b2cPutData`;
      default:
        return '';
    }
  }

  private parseSOAPResponse(xmlResponse: string): any {
    // Simple XML parsing - in production, use a proper XML parser
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlResponse, 'text/xml');
      
      // Extract the response data from SOAP envelope
      const soapBody = xmlDoc.getElementsByTagName('soap:Body')[0];
      if (soapBody) {
        return this.xmlToJson(soapBody);
      }
      return null;
    } catch (error) {
      console.error('Error parsing SOAP response:', error);
      return null;
    }
  }

  private xmlToJson(xml: Element): any {
    const result: any = {};
    
    if (xml.hasChildNodes()) {
      for (let i = 0; i < xml.childNodes.length; i++) {
        const child = xml.childNodes[i] as Element;
        if (child.nodeType === 1) { // Element node
          if (result[child.nodeName]) {
            if (!Array.isArray(result[child.nodeName])) {
              result[child.nodeName] = [result[child.nodeName]];
            }
            result[child.nodeName].push(this.xmlToJson(child));
          } else {
            result[child.nodeName] = this.xmlToJson(child);
          }
        } else if (child.nodeType === 3) { // Text node
          return child.textContent;
        }
      }
    }
    
    return result;
  }

  async getDealershipInfo(): Promise<ApiResponse<DealershipInfo>> {
    try {
      // Step 1: Get retail locations (dealership info)
      const locationResponse = await this.getRetailLocations();
      if (!locationResponse.success) {
        throw new Error(locationResponse.error);
      }

      // Step 2: Get schedulable service packages
      const servicesResponse = await this.getSchedulableServicePackages();
      
      // Combine the data
      const location = locationResponse.data;
      if (!location) {
        throw new Error('No location data received from TireWorksHD API');
      }
      const services = servicesResponse.success && servicesResponse.data ? servicesResponse.data : [];

      const dealership: DealershipInfo = {
        id: location.locationId.toString(),
        name: location.name,
        address: location.address1,
        city: location.city,
        state: location.state,
        zipCode: location.zipId,
        phone: location.phone,
        email: location.emailAddress || '',
        businessHours: {
          // Default business hours - TireWorksHD doesn't provide this in the API
          monday: { open: '08:00', close: '18:00' },
          tuesday: { open: '08:00', close: '18:00' },
          wednesday: { open: '08:00', close: '18:00' },
          thursday: { open: '08:00', close: '18:00' },
          friday: { open: '08:00', close: '18:00' },
          saturday: { open: '08:00', close: '17:00' },
          sunday: { closed: true, open: '', close: '' }
        },
        services: services
      };

      return {
        success: true,
        data: dealership
      };
    } catch (error) {
      console.error('Error fetching dealership info:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch dealership info'
      };
    }
  }

  private async getRetailLocations(): Promise<ApiResponse<any>> {
    try {
      const soapBody = `
        <eXceedb2cRequest xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema">
          <GetRetailLocations>
            <companyId>1</companyId>
            <locationId>${this.config.locationId || this.config.dealershipId || '1'}</locationId>
            <ApplicationUID>${this.config.applicationUID || this.config.accessKey || '90CE6794-3F04-4D63-82F5-CA7AEBFFE38D'}</ApplicationUID>
          </GetRetailLocations>
        </eXceedb2cRequest>
      `;

      const response = await this.makeSOAPRequest('b2cGetData', soapBody);
      
      // Parse the XML response to extract location data
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(response, 'text/xml');
      
      const retailLocation = xmlDoc.querySelector('RetailLocation');
      if (!retailLocation) {
        throw new Error('No retail location found in response');
      }

      return {
        success: true,
        data: {
          name: retailLocation.querySelector('Name')?.textContent || '',
          locationId: retailLocation.querySelector('locationId')?.textContent || '1',
          address1: retailLocation.querySelector('Address1')?.textContent || '',
          address2: retailLocation.querySelector('Address2')?.textContent || '',
          phone: retailLocation.querySelector('Phone')?.textContent || '',
          zipId: retailLocation.querySelector('ZipId')?.textContent || '',
          emailAddress: retailLocation.querySelector('EmailAddress')?.textContent || '',
          city: retailLocation.querySelector('City')?.textContent || '',
          state: retailLocation.querySelector('State')?.textContent || ''
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get retail locations'
      };
    }
  }

  private async getSchedulableServicePackages(): Promise<ApiResponse<ServiceType[]>> {
    try {
      const soapBody = `
        <eXceedb2cRequest xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema">
          <GetSchedulableServicePackages>
            <companyId>1</companyId>
            <locationId>${this.config.locationId || this.config.dealershipId || '1'}</locationId>
            <ApplicationUID>${this.config.applicationUID || this.config.accessKey || '90CE6794-3F04-4D63-82F5-CA7AEBFFE38D'}</ApplicationUID>
          </GetSchedulableServicePackages>
        </eXceedb2cRequest>
      `;

      const response = await this.makeSOAPRequest('b2cGetData', soapBody);
      
      // Parse the XML response to extract service packages
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(response, 'text/xml');

      // TireWorks responses may use RetailOrderPackage OR RetailServicePackage
      const servicePackages = xmlDoc.querySelectorAll('RetailOrderPackage, RetailServicePackage');
      const services: ServiceType[] = [];

      servicePackages.forEach(pkg => {
        const packageCode = pkg.querySelector('PackageCode')?.textContent || '';
        const title = pkg.querySelector('Title')?.textContent || '';
        const description = pkg.querySelector('Description')?.textContent || '';
        const durationText =
          pkg.querySelector('ServiceDuration')?.textContent ||
          pkg.querySelector('EstimatedMinutes')?.textContent ||
          '60';
        const duration = parseInt(durationText, 10) || 60;

        if (!packageCode) {
          console.warn(`Service package found without PackageCode: ${title}`);
          return;
        }

        services.push({
          id: packageCode,
          name: title,
          description: description,
          estimatedDuration: duration,
          price: 0, // Price not provided in service packages endpoint
          category: this.categorizeService(title)
        });
      });

      if (services.length === 0) {
        throw new Error('No service packages found in TireWorks response');
      }

      return {
        success: true,
        data: services
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get service packages'
      };
    }
  }

  private categorizeService(serviceName: string): 'tire' | 'maintenance' | 'repair' | 'inspection' {
    const name = serviceName.toLowerCase();
    if (name.includes('tire') || name.includes('wheel') || name.includes('alignment')) {
      return 'tire';
    } else if (name.includes('oil') || name.includes('filter') || name.includes('maintenance')) {
      return 'maintenance';
    } else if (name.includes('brake') || name.includes('inspect')) {
      return 'inspection';
    } else {
      return 'repair';
    }
  }

  async getAvailableTimeSlots(date: string, serviceIds: string[]): Promise<ApiResponse<TimeSlot[]>> {
    try {
      // Step 1: Get location calendars
      const calendarsResponse = await this.getLocationCalendars();
      if (!calendarsResponse.success) {
        throw new Error(calendarsResponse.error);
      }

      // Step 2: Get calendar openings for the default calendar (or first available)
      const calendars = calendarsResponse.data;
      if (!calendars || calendars.length === 0) {
        throw new Error('No calendars available for this location');
      }

      // Use the first calendar or find a "Web" calendar
      const webCalendar = calendars.find(cal => cal.calendarName.toLowerCase().includes('web'));
      const selectedCalendar = webCalendar || calendars[0];

      // Store the selected calendar ID for later use
      const calendarId = selectedCalendar.calendarId;

      const openingsResponse = await this.getCalendarOpenings(calendarId, date, date);
      if (!openingsResponse.success) {
        throw new Error(openingsResponse.error);
      }

      return {
        success: true,
        data: openingsResponse.data
      };
    } catch (error) {
      console.error('Error fetching time slots:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch available time slots'
      };
    }
  }

  private async getLocationCalendars(): Promise<ApiResponse<any[]>> {
    try {
      const soapBody = `
        <eXceedb2cRequest>
          <GetLocationCalendars>
            <CompanyId>1</CompanyId>
            <LocationId>${this.config.locationId || this.config.dealershipId || '1'}</LocationId>
            <ApplicationUID>${this.config.applicationUID || this.config.accessKey || '90CE6794-3F04-4D63-82F5-CA7AEBFFE38D'}</ApplicationUID>
          </GetLocationCalendars>
        </eXceedb2cRequest>
      `;

      const response = await this.makeSOAPRequest('b2cGetData', soapBody);

      // Parse the XML response to extract calendar data
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(response, 'text/xml');

      const calendarItems = xmlDoc.querySelectorAll('CalendarItem, LocationCalendar');
      const calendars: any[] = [];

      calendarItems.forEach(item => {
        calendars.push({
          calendarId: item.querySelector('CalendarId')?.textContent || item.querySelector('CalendarID')?.textContent || '',
          locationId: item.querySelector('LocationId')?.textContent || item.querySelector('locationId')?.textContent || '',
          locationName: item.querySelector('LocationName')?.textContent || item.querySelector('locationName')?.textContent || '',
          calendarName: item.querySelector('CalendarName')?.textContent || item.querySelector('calendarName')?.textContent || '',
          calendarDescription: item.querySelector('CalendarDescription')?.textContent || item.querySelector('calendarDescription')?.textContent || ''
        });
      });

      return {
        success: true,
        data: calendars
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get location calendars'
      };
    }
  }

  private async getCalendarOpenings(calendarId: string, startDate: string, endDate: string): Promise<ApiResponse<TimeSlot[]>> {
    try {
      const soapBody = `
        <eXceedb2cRequest>
          <GetCalendarOpenings>
            <companyId>1</companyId>
            <locationId>${this.config.locationId || this.config.dealershipId || '1'}</locationId>
            <ApplicationUID>${this.config.applicationUID || this.config.accessKey || '90CE6794-3F04-4D63-82F5-CA7AEBFFE38D'}</ApplicationUID>
            <CalendarId>${calendarId}</CalendarId>
            <StartDate>${startDate}</StartDate>
            <EndDate>${endDate}</EndDate>
          </GetCalendarOpenings>
        </eXceedb2cRequest>
      `;

      const response = await this.makeSOAPRequest('b2cGetData', soapBody);
      
      // Parse the XML response to extract appointment slots
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(response, 'text/xml');
      
      // Different TireWorks deployments may return different node names
      const appointmentSlots = xmlDoc.querySelectorAll('AppointmentSlot, CalendarOpening, Opening');
      const timeSlots: TimeSlot[] = [];

      appointmentSlots.forEach(slot => {
        const slotCalendarId = slot.querySelector('CalendarId')?.textContent || slot.querySelector('CalendarID')?.textContent || '';
        const slotDate = slot.querySelector('SlotDate')?.textContent || slot.querySelector('Date')?.textContent || '';
        const startTime = slot.querySelector('SlotStartTime')?.textContent || slot.querySelector('StartTime')?.textContent || slot.querySelector('StartDateTime')?.textContent || '';
        const endTime = slot.querySelector('SlotEndTime')?.textContent || slot.querySelector('EndTime')?.textContent || slot.querySelector('EndDateTime')?.textContent || '';

        if (slotDate && startTime && endTime) {
          // Parse the datetime strings to extract date and time
          const startDateTime = new Date(startTime.includes('T') ? startTime : `${slotDate}T${startTime}`);
          const endDateTime = new Date(endTime.includes('T') ? endTime : `${slotDate}T${endTime}`);
          // Generate 30-minute slots within the available window
          let currentTime = new Date(startDateTime);
          while (currentTime < endDateTime) {
            const timeString = currentTime.toTimeString().substring(0, 5); // HH:MM format
            const dateString = currentTime.toISOString().substring(0, 10); // YYYY-MM-DD format

            timeSlots.push({
              id: `${dateString}-${timeString}`,
              date: dateString,
              time: timeString,
              available: true,
              duration: 30, // 30-minute slots
              calendarId: slotCalendarId || calendarId
            });

            // Move to next 30-minute slot
            currentTime.setMinutes(currentTime.getMinutes() + 30);
          }
        }
      });

      return {
        success: true,
        data: timeSlots
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get calendar openings'
      };
    }
  }

  async createAppointment(appointment: Appointment): Promise<ApiResponse<{ appointmentId: string }>> {
    try {
      // Parse the appointment date and time
      const appointmentDateTime = new Date(`${appointment.timeSlot.date}T${appointment.timeSlot.time}:00`);
      
      // Build the SOAP body for CreateNewAppointment
      const soapBody = `
        <eXceedb2cRequest xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema">
          <CreateNewAppoinment>
            <companyId>1</companyId>
            <locationId>${this.config.locationId || this.config.dealershipId || '1'}</locationId>
            <applicationUID>${this.config.applicationUID || this.config.accessKey || '90CE6794-3F04-4D63-82F5-CA7AEBFFE38D'}</applicationUID>
            <appointmentYear>${appointmentDateTime.getFullYear()}</appointmentYear>
            <appointmentMonth>${(appointmentDateTime.getMonth() + 1).toString().padStart(2, '0')}</appointmentMonth>
            <appointmentDay>${appointmentDateTime.getDate().toString().padStart(2, '0')}</appointmentDay>
            <appointmentHour>${appointmentDateTime.getHours().toString().padStart(2, '0')}</appointmentHour>
            <appointmentMinute>${appointmentDateTime.getMinutes().toString().padStart(2, '0')}</appointmentMinute>
            <customerId></customerId>
            <customerMemberId></customerMemberId>
            <customerVehicleId></customerVehicleId>
            <calendarId>${appointment.timeSlot.calendarId || ''}</calendarId>
            <customerFirstName>${appointment.customer.firstName}</customerFirstName>
            <customerLastName>${appointment.customer.lastName}</customerLastName>
            <customerPhone>${appointment.customer.phone}</customerPhone>
            <customerEmail>${appointment.customer.email}</customerEmail>
            <customerAddress1>${appointment.customer.address || ''}</customerAddress1>
            <customerAddress2></customerAddress2>
            <customerCity>${appointment.customer.city || ''}</customerCity>
            <customerState>${appointment.customer.state || ''}</customerState>
            <customerZip>${appointment.customer.zipCode || ''}</customerZip>
            <isCustomerWaiting>false</isCustomerWaiting>
            <comment>${appointment.notes || ''}</comment>
            <RetailServicePackages>
              ${appointment.services.map(service => `
              <RetailServicePackage>
                <PackageCode>${service.twhdPackageCode || service.id}</PackageCode>
              </RetailServicePackage>`).join('')}
            </RetailServicePackages>
          </CreateNewAppoinment>
        </eXceedb2cRequest>
      `;

      const response = await this.makeSOAPRequest('b2cPutData', soapBody);
      
      // Parse the XML response to extract appointment ID
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(response, 'text/xml');
      
      const statusCode = xmlDoc.querySelector('responseStatusCode')?.textContent;
      const message = xmlDoc.querySelector('responseMessage')?.textContent;
      const orderId = xmlDoc.querySelector('responseOrderId')?.textContent;

      if (statusCode === '200' && orderId) {
        return {
          success: true,
          data: { appointmentId: orderId },
          message: message || 'Appointment created successfully'
        };
      } else {
        throw new Error(message || 'Failed to create appointment');
      }
    } catch (error) {
      console.error('Error creating appointment:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create appointment'
      };
    }
  }

  private getMockServices(): ServiceType[] {
    return [
      {
        id: 'tire-install',
        name: 'Tire Installation',
        description: 'Professional tire mounting and balancing',
        estimatedDuration: 60,
        price: 25,
        category: 'tire'
      },
      {
        id: 'oil-change',
        name: 'Oil Change',
        description: 'Full synthetic oil change and filter replacement',
        estimatedDuration: 30,
        price: 45,
        category: 'maintenance'
      },
      {
        id: 'brake-inspection',
        name: 'Brake Inspection',
        description: 'Complete brake system inspection',
        estimatedDuration: 45,
        price: 35,
        category: 'inspection'
      },
      {
        id: 'wheel-alignment',
        name: 'Wheel Alignment',
        description: 'Four-wheel alignment service',
        estimatedDuration: 90,
        price: 89,
        category: 'tire'
      },
      {
        id: 'tire-rotation',
        name: 'Tire Rotation',
        description: 'Rotate tires for even wear',
        estimatedDuration: 30,
        price: 20,
        category: 'tire'
      }
    ];
  }

  private generateMockTimeSlots(date: string): TimeSlot[] {
    const slots: TimeSlot[] = [];
    const startHour = 8;
    const endHour = 17;
    const slotDuration = 30; // 30-minute slots

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += slotDuration) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const isAvailable = Math.random() > 0.3; // 70% chance of being available

        slots.push({
          id: `${date}-${time}`,
          date,
          time,
          available: isAvailable,
          duration: slotDuration
        });
      }
    }

    return slots;
  }
}

export default TireWorksHDApi;